import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import DownArrow from "../../assets/svgs/down-arrow.svg";
import Delete from "../../assets/svgs/Recycle Bin.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialClosedFilterData } from "../../utils/initialData";
import ClosedArchiveFilter from "./ArchiveFilter/ClosedArchiveFilter";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const ClosedArchive = () => {
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);
  const [config] = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [selectedItem, setSelectedItem] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState("closing_date");
  const [expandedRows, setExpandedRows] = useState({});
  const [sortDirection, setSortDirection] = useState("desc");
  const [disableDelete, setDisableDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paginationData, setPaginationData] = useState({ totalItems: 0, from: 0, to: 0, totalPages: 0 });

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : initialClosedFilterData
  });

  // ==================== Debounced Search ====================
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState("");

  // Trigger API only when >= 3 chars or cleared
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const listIds = listData?.map((el) => el.id);

  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      const encodedSearch = encodeURIComponent(search);
      params += `&search=${encodedSearch}`;
    }
    const { count, start_count, end_count } = filters;
    if (count !== undefined) {
      params += `&count=${encodeURIComponent(count)}`;
    }
    if (start_count !== undefined) {
      params += `&start_count=${encodeURIComponent(start_count)}`;
    }
    if (end_count !== undefined) {
      params += `&end_count=${encodeURIComponent(end_count)}`;
    }
    if (sortDirection !== undefined && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`;
    }
    if (sortBy !== "id" && sortBy !== undefined) {
      params += `&sort_by=${sortBy}`;
    }

    localStorage.setItem("queryParams", params);

    return params;
  };

  const fetchData = (filters = filterData) => {
    const queryParams = buildQueryParams(filters)
    axios
      .get(`${BASE_URL}/archive-listing?${queryParams}&type=closed`, config)
      .then((res) => {
        const value = res?.data?.closelist;
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
        setSelectedItem([]);
        setIsSelectAll(false);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => {
        setLoading(false);
        setSelectedId("");
      });
  };

  useEffect(() => {
    fetchData();
  }, [sortBy, sortDirection]);

  useEffect(() => {
    onSuccess();
  }, [currentPage, itemPerPage, search]);

  const onSuccess = () => {
    setLoading(true);
    fetchData();
  };

  const handleSelectCheck = (id) => {
    if (selectedItem.length === 0) {
      setSelectedItem([id]);
    } else {
      const index = selectedItem?.indexOf(id);
      if (index === -1) {
        setSelectedItem([...selectedItem, id]);
      } else {
        const filterData = selectedItem?.filter((el) => el !== id);
        setSelectedItem(filterData);
      }
    }
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleDelete = () => {
    const dataToSend = {
      action: "delete",
      type: "closed",
      ids: [selectedId],
    };
    setDisableDelete(true);

    axios
      .post(`${BASE_URL}/handle-bulkaction`, dataToSend, config)
      .then((res) => {
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };


  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">
          Closed Archive
          {selectedItem?.length > 0 && (
            <span className="body-L ml-2">
              ({selectedItem?.length} <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
            </span>
          )}
        </p>
        <div className="md:flex block gap-6 items-center">
          <div className="flex gap-6 justify-between items-center">
            <ClosedArchiveFilter
              filterData={filterData}
              onSetFilterData={(value) => {
                setFilterData(value);
              }}
              from="Closed"
              onCallApiAgain={(filters) => onSuccess(filters)}
            />
            {/* Updated Search Box */}
            <div className="search-box contacts relative">
              <input
                type="text"
                className="body-S"
                placeholder="Search name"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="light-bg-L pb-3 table-container">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
          {loading ? (
            <Loader />
          ) : listData?.length === 0 ? (
            <p className="body-N text-center mt-6 p-10">No closed Found</p>
          ) : (
             <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th><p>Details</p></th>
                    <th>
                      <div className="table-header">
                        Closing Date
                        <img role="button" src={sortBy !== "closing_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("closing_date"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th className="green-H">
                      <div className="table-header">
                        Property Name
                        <img role="button" src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("property_name"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                       <th>
                      <div className="table-header">
                          Acquisition
                        <img
                          role="button"
                          src={sortBy !== "acquistion" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("acquistion");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Disposition
                        <img
                          role="button"
                          src={sortBy !== "disposition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("disposition");
                            if (sortDirection === "desc") {
                              setSortDirection("asc");
                            } else {
                              setSortDirection("desc");
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Sale Price
                        <img role="button" src={sortBy !== "sale_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("sale_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Sale Cap Rate
                        <img role="button" src={sortBy !== "sale_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("sale_cap_rate"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Gci to company
                        <img role="button" src={sortBy !== "gci_to_company" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("gci_to_company"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {listData.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="dark-H body-N">
                        <td>
                          <img role="button" src={expandedRows[el.id] ? UpArrow : DownArrow} alt="toggle" className="sidebar-icons" onClick={() => toggleRowDetails(el.id)} />
                        </td>
                        <td className="text-center">{el?.close_date}</td>
                        <td className="head-5 capitalize">
                          <Link
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (el?.portfolio === 0) navigate(`/property/${el?.contract?.executed?.property_id}`);
                              else if (el?.contract?.executed?.link?.owner_info_type === "contact") navigate(`/contact/${el?.contract?.executed?.link?.owner?.id}`);
                              else if (el?.contract?.executed?.link?.owner_info_type === "company") navigate(`/company/${el?.contract?.executed?.link?.owner?.id}`);
                            }}
                          >
                            {el?.contract?.executed?.link?.property_name}<br />
                            {el?.contract?.executed?.link?.street_address}<br />
                            {el?.contract?.executed?.link?.city && el?.contract?.executed?.link?.state ? `${el.contract.executed.link.city}, ${el.contract.executed.link.state}` : ""}
                          </Link>
                        </td>
                           <td>
                                    <Link
                                      to={`/contact/${el?.contract?.executed?.offer?.buyer_contact_id}`}
                                      className="body-N  dark-H "
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/contact/${el?.contract?.executed?.offer?.buyer_contact_id}`);
                                      }}
                                    >
                                      {el?.contract?.executed?.offer?.acquistion_name || " "}
                                    </Link>
                                  </td>

                                  
                                  <td>
                                    <Link
                                      to={`/contact/${el?.contract?.executed?.offer?.seller_contact_id}`}
                                      className="body-N  dark-H "
                                      onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/contact/${el?.contract?.executed?.offer?.seller_contact_id}`);
                                      }}
                                    >
                                      {el?.contract?.executed?.offer?.disposition_name || " "}
                                    </Link>
                                  </td>

                        <td className="text-center">${el?.contract_price_format}</td>
                        <td className="text-center">{el?.contract_cap_rate_format}%</td>
                        <td className="text-center">${el?.contract?.executed?.gross_commission?.gci_to_company}</td>
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">

                                <div className="grid grid-cols-8 gap-6 items-start">

                               

                                  <div>
                                    <p className="dark-H head-4 mb-2">Initial List Date</p>
                                    <p className="body-N  dark-H">
                                      {moment(el?.inital_date).tz('America/New_York').format("MM/DD/YYYY")}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Contract Date</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract_date || ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.contract?.executed?.offer?.seller_broker?.length > 0 ? (
                                        el.contract.executed.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.contract.executed.offer.seller_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract?.executed?.offer?.seller_bds?.first_name} {el?.contract?.executed?.offer?.seller_bds?.last_name || " "}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.contract?.executed?.offer?.buyer_broker?.length > 0 ? (
                                        el.contract.executed.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.contract.executed.offer.buyer_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract?.executed?.offer?.buyer_bds?.first_name} {el?.contract?.executed?.offer?.buyer_bds?.last_name || " "}
                                    </p>
                                  </div>


                                </div>

                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.contract?.executed?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el?.contract?.executed?.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.contract?.executed?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el?.contract?.executed?.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
          )}
        </div>

        <ContactPagination
          from="archive"
          setSelectedItem={setSelectedItem}
          selectedItem={selectedItem}
          paginationData={paginationData}
          handleNext={handleNext}
          handlePrev={handlePrev}
          onSuccess={onSuccess}
          itemPerPage={itemPerPage}
          handleItemPerPageSelect={(value) => {
            setItemPerPage(value);
            setCurrentPage(1);
          }}
        />

        <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
      </div>
    </BaseLayout>
  );
};

export default ClosedArchive;